import {
   BLOCKS,
   type Block,
   type Document,
   INLINES,
   type Inline,
   TOP_LEVEL_BLOCKS,
   type TopLevelBlock,
   type TopLevelBlockEnum
} from "@contentful/rich-text-types";
import type { EntrySys, Metadata, TagLink, TaxonomyConceptLink } from "contentful";
import * as z from "zod";

export type JsonPrimitive = string | number | boolean | null;
export type JsonObject = { [Key in string]: JsonValue } & { [Key in string]?: JsonValue | undefined };
export type JsonArray = JsonValue[] | readonly JsonValue[];
export type JsonValue = JsonPrimitive | JsonObject | JsonArray;

export const ZJsonPrimitive: z.ZodType<JsonPrimitive> = z.lazy(() => z.string().or(z.number()).or(z.boolean()).nullable());
export const ZJsonObject: z.ZodType<JsonObject> = z.lazy(() =>
   z.record(
      z.string(),
      z.lazy(() => ZJsonValue)
   )
);
export const ZJsonArray: z.ZodType<JsonArray> = z.lazy(() => z.array(z.lazy(() => ZJsonValue)));
export const ZJsonValue: z.ZodType<JsonValue> = z.lazy(() => ZJsonPrimitive.or(ZJsonObject).or(ZJsonArray));

export const ZTagLink: z.ZodType<TagLink> = z.object({
   type: z.literal("Link"),
   linkType: z.literal("Tag"),
   id: z.string()
});

export const ZTaxonomyConceptLink: z.ZodType<TaxonomyConceptLink> = z.object({
   type: z.literal("Link"),
   linkType: z.literal("TaxonomyConcept"),
   id: z.string()
});

export const ZMetadata: z.ZodType<Metadata> = z.object({
   tags: z.array(
      z.object({
         sys: ZTagLink
      })
   ),
   concepts: z
      .array(
         z.object({
            sys: ZTaxonomyConceptLink
         })
      )
      .optional()
});
const contentTypes = [
   "adventCalendarSlot",
   "contestForm",
   "imageWithFocus",
   "v2_article",
   "v2_section",
   "productCategory",
   "segment",
   "recipe",
   "footer",
   "externalLink",
   "linkToMedia",
   "warning"
] as const;

type ContentType = (typeof contentTypes)[number];

export const ZContentfulDate = z.templateLiteral([
   z.number(),
   "-",
   z.number(),
   "-",
   z.number(),
   "T",
   z.number(),
   ":",
   z.number(),
   ":",
   z.number(),
   "Z"
]);

export const createZEntrySys = (
   contentType: ContentType,
   properties: (keyof EntrySys)[]
): z.ZodType<Pick<EntrySys, (typeof properties)[number]>> => {
   const ZFullEntrySys = z.object({
      contentType: z.object({
         sys: z.object({
            type: z.literal("Link"),
            linkType: z.literal("ContentType"),
            id: z.literal(contentType)
         })
      }),
      id: z.string(),
      type: z.literal("Entry"),
      createdAt: ZContentfulDate,
      updatedAt: ZContentfulDate,
      revision: z.number(),
      space: z.object({
         sys: z.object({
            type: z.literal("Link"),
            linkType: z.literal("Space"),
            id: z.string()
         })
      }),
      environment: z.object({
         sys: z.object({
            type: z.literal("Link"),
            linkType: z.literal("Environment"),
            id: z.string()
         })
      }),
      locale: z.string().optional(),
      publishedVersion: z.number()
   });
   if (properties.length) {
      const pickObject: Record<keyof typeof ZFullEntrySys.shape, true> = properties.reduce(
         (acc, prop) => {
            acc[prop as keyof typeof ZFullEntrySys.shape] = true;
            return acc;
         },
         {} as Record<keyof typeof ZFullEntrySys.shape, true>
      );

      return ZFullEntrySys.pick(pickObject);
   }
   return ZFullEntrySys;
};

export const ZAssetSys = z.object({
   id: z.string(),
   type: z.literal("Asset")
});

export const ZText = z.object({
   nodeType: z.literal("text"),
   value: z.string(),
   marks: z.array(
      z.object({
         type: z.string()
      })
   ),
   data: z.object()
});

export const ZInlineNodeType: z.ZodType<INLINES> = z.enum(INLINES);
export const ZBlockNodeType: z.ZodType<BLOCKS> = z.enum(BLOCKS);
export const ZTopLevelBlockNodeType: z.ZodType<TopLevelBlockEnum> = z.enum(TOP_LEVEL_BLOCKS);

export const ZInline: z.ZodType<Inline> = z.object({
   nodeType: ZInlineNodeType,
   content: z.array(z.lazy(() => ZInline).or(ZText)),
   data: z.object()
});
export const ZBlock: z.ZodType<Block> = z.object({
   nodeType: ZBlockNodeType,
   content: z.array(z.lazy(() => ZBlock.or(ZInline).or(ZText))),
   data: z.object()
});
export const ZTopLevelBlock: z.ZodType<TopLevelBlock> = z.object({
   nodeType: ZTopLevelBlockNodeType,
   content: z.array(z.lazy(() => ZBlock.or(ZInline).or(ZText))),
   data: z.object()
});
export const ZRichText: z.ZodType<Document> = z.object({
   nodeType: z.literal(BLOCKS.DOCUMENT),
   content: z.array(z.lazy(() => ZTopLevelBlock)),
   data: z.object()
});

export const createZContentfulEntry = <T extends z.ZodTypeAny>(
   fields: T,
   contentType: ContentType,
   sysProperties: (keyof EntrySys)[]
) =>
   z.object({
      fields,
      metadata: ZMetadata,
      sys: createZEntrySys(contentType, sysProperties)
   });

export const ZDimensions = z.object({
   width: z.number(),
   height: z.number()
});

export const ZFileDetails = z.object({
   image: ZDimensions.optional(),
   size: z.number()
});
export const ZFile = z.object({
   contentType: z.string(),
   details: ZFileDetails,
   fileName: z.string(),
   url: z.string()
});
export const ZImageFields = z.object({
   description: z.string().nullish(),
   file: ZFile,
   title: z.string()
});

export const ZSegmentFields = z.object({
   title: z.string().optional(),
   customerGroups: z.array(z.string()).optional(),
   chains: z.array(z.string()).optional(),
   customerNumbers: z.array(z.string()).optional(),
   customerWarehouses: z.array(z.string()).optional(),
   loggedIn: z.boolean().optional(),
   punchoutCustomer: z.boolean().optional(),
   voucherifyId: z.string().optional()
});

export const ZSegmentEntry = createZContentfulEntry(ZSegmentFields, "segment", []);

export const ZExternalLinkFields = z.object({
   linkText: z.string(),
   url: z.string()
});

export const ZExternalLinkEntry = createZContentfulEntry(ZExternalLinkFields, "externalLink", []);

export const ZImageAsset = z.object({
   fields: ZImageFields,
   metadata: ZMetadata,
   sys: ZAssetSys
});

export const ZImageWithFocusFields = z.object({
   altText: z.string(),
   focalPoint: ZJsonObject.or(ZJsonArray).optional(),
   image: ZImageAsset
});

export const ZImageWithFocusEntry = createZContentfulEntry(ZImageWithFocusFields, "imageWithFocus", []);

export const ZLinkToMediaFields = z.object({
   linkText: z.string(),
   media: ZImageAsset
});

export const ZLinkToMediaEntry = createZContentfulEntry(ZLinkToMediaFields, "linkToMedia", []);

export const ZArticleFundamentalFields = z.object({
   callToAction: z.string().nullish(),
   callToActionSubTitle: z.string().nullish(),
   headerImage: ZImageWithFocusEntry.nullish(),
   preface: z.string().nullish(),
   title: z.string(),
   orderPriority: z.number().optional()
});
export const ZArticleFundamentalEntry = createZContentfulEntry(ZArticleFundamentalFields, "v2_article", [
   "id",
   "contentType",
   "type"
]);

export const ZProductCategoryFields = z.object({
   contentfulName: z.string().nullish(),
   categoryUrls: z.array(z.string()),
   body: ZRichText,
   includeSegments: ZSegmentEntry.optional().array().optional(),
   excludeSegments: ZSegmentEntry.optional().array().optional()
});

export const ZProductCategoryEntry = createZContentfulEntry(ZProductCategoryFields, "productCategory", [
   "id",
   "contentType",
   "type"
]);

export const ZRecipeFundamentalFields = z.object({
   title: z.string(),
   orderPriority: z.number().optional(),
   recipeData: ZJsonObject.nullish()
});

export const ZRecipeFundamentalEntry = createZContentfulEntry(ZRecipeFundamentalFields, "recipe", ["id", "contentType", "type"]);

export const ZArticleUrlDataFields = z.object({
   title: z.string(),
   slug: z.string(),
   parentId: z.string().optional(),
   excludeSegments: ZSegmentEntry.optional().array().optional(),
   includeSegments: ZSegmentEntry.optional().array().optional(),
   visibleInSections: z.array(z.string()).optional()
});

export const ZArticleUrlDataEntry = createZContentfulEntry(ZArticleUrlDataFields, "v2_article", ["id", "contentType", "type"]);

export const ZRecipeUrlDataFields = z.object({
   title: z.string(),
   slug: z.string(),
   parentId: z.string().optional(),
   visibleInSections: z.array(z.string()).optional()
});

export const ZRecipeUrlDataEntry = createZContentfulEntry(ZRecipeUrlDataFields, "recipe", ["id", "contentType", "type"]);

export const ZAdventCalendarSlotUrlDataFields = z.object({
   title: z.string(),
   slug: z.string(),
   parentId: z.string().optional()
});

export const ZAdventCalendarSlotUrlDataEntry = createZContentfulEntry(ZAdventCalendarSlotUrlDataFields, "adventCalendarSlot", [
   "id",
   "contentType",
   "type"
]);

export const ZSectionUrlDataFields = z.object({
   title: z.string(),
   slug: z.string(),
   parentId: z.string().optional(),
   description: z.string().optional(),
   icon: ZImageAsset.nullish()
});

export const ZSectionUrlDataEntry = createZContentfulEntry(ZSectionUrlDataFields, "v2_section", ["id", "contentType", "type"]);

export const ZArticleFields = z.object({
   title: z.string(),
   callToAction: z.string().nullish(),
   callToActionSubTitle: z.string().nullish(),
   headerImage: ZImageWithFocusEntry.nullish(),
   preface: z.string().nullish(),
   body: ZRichText,
   metaTitle: z.string().nullish(),
   metaDescription: z.string().nullish(),
   slug: z.string(),
   includeSegments: ZSegmentEntry.array().optional(),
   excludeSegments: ZSegmentEntry.array().optional(),
   targetAudience: ZSegmentEntry.array().optional(),
   articleType: z
      .enum(["Enkel tittel og ingress", "Standard-utseende", "Stort bilde, header og ingress", "Uten bilde, header og ingress"])
      .nullish(),
   orderPriority: z.number().optional(),
   visibleInSections: z.array(z.string()).nullish(),
   parentId: z.string().optional(),
   searchCollections: z.array(z.enum(["artikler", "inspirasjon"])).nullish()
});

export const ZSectionFields = z.object({
   title: z.string(),
   slug: z.string(),
   parentId: z.string().optional(),
   description: z.string().optional(),
   icon: ZImageAsset.nullish()
});

export const ZRecipeFields = z.object({
   title: z.string(),
   slug: z.string(),
   orderPriority: z.number().optional(),
   parentId: z.string().optional(),
   visibleInSections: z.array(z.string()).optional(),
   recipeData: ZJsonObject.nullish(),
   searchCollections: z.array(z.enum(["oppskrifter"])).nullish()
});

export const ZContestFormFields = z.object({
   internalName: z.string(),
   question: z.string(),
   options: z.array(z.string()),
   correctOption: z.string(),
   signUpCode: z.string(),
   validFrom: z.string().optional(),
   validTo: z.string().optional(),
   messageNotValidParticipated: ZRichText.optional(),
   messageNotValidNotParticipated: ZRichText.optional()
});

export const ZContestFormEntry = createZContentfulEntry(ZContestFormFields, "contestForm", []);

export const ZAdventCalendarSlotFields = ZAdventCalendarSlotUrlDataFields.extend({
   body: ZRichText.optional(),
   slotImage: ZImageAsset.optional(),
   behindSlotImage: ZImageAsset.optional(),
   question: ZContestFormEntry.optional(),
   prizeImage: ZImageAsset.optional(),
   prizeText: z.string().optional(),
   date: z.string().optional(),
   visibleInSections: z.array(z.string()).optional()
});

export const ZAdventCalendarSlotEntry = createZContentfulEntry(ZAdventCalendarSlotFields, "adventCalendarSlot", []);

export const ZSectionEntry = createZContentfulEntry(ZSectionFields, "v2_section", []);

export const ZArticleEntry = createZContentfulEntry(ZArticleFields, "v2_article", []);

export const ZRecipeEntry = createZContentfulEntry(ZRecipeFields, "recipe", []);

export const ZFullContentEntry = ZArticleEntry.or(ZSectionEntry).or(ZRecipeEntry).or(ZAdventCalendarSlotEntry);

export const ZFooterContentFields = z.object({
   contactNumber: z.string().nullish(),
   columnCustomerServiceLabel: z.string().nullish(),
   openingHours: ZJsonValue.nullish(),
   column2label: z.string().nullish(),
   column3label: z.string().nullish(),
   column4label: z.string().nullish(),
   column2links: z.array(ZArticleEntry.or(ZExternalLinkEntry).or(ZLinkToMediaEntry)).nullish(),
   column3links: z.array(ZArticleEntry.or(ZExternalLinkEntry).or(ZLinkToMediaEntry)).nullish(),
   column4links: z.array(ZArticleEntry.or(ZExternalLinkEntry).or(ZLinkToMediaEntry)).nullish(),
   termsAndConditions: z.array(ZArticleEntry.or(ZExternalLinkEntry).or(ZLinkToMediaEntry)).nullish()
});

export const ZFooterEntry = createZContentfulEntry(ZFooterContentFields, "footer", ["id", "type"]);

export const ZWarningFields = z.object({
   internalName: z.string(),
   message: ZRichText,
   theme: z.enum(["Important", "Information", "Warning"]),
   warningtype: z.array(
      z.enum([
         "I handlekurven når den inneholder valgte produkter",
         "Produktdetaljsiden for valgte produkter",
         "Toppen av alle sider"
      ])
   ),
   visibleFor: z.enum(["alertify", "everyone", "loggedInCustomer"]).optional(),
   skus: z.array(z.string()).optional(),
   includeSegments: ZSegmentEntry.array().optional(),
   excludeSegments: ZSegmentEntry.array().optional()
});

export const ZWarningEntry = createZContentfulEntry(ZWarningFields, "warning", ["id", "type", "contentType"]);

export const ZCallToActionListFields = z.object({
   internalName: z.string(),
   title: z.string().optional(),
   contentList: z.array(z.object()).optional(), // EntryLink for Accordion, ACSlot, ImageLink, ImageLinkToUrl, Recipe, Usp, Article
   maxVisible: z.number().optional(),
   includeSegments: ZSegmentEntry.array().optional(),
   excludeSegments: ZSegmentEntry.array().optional(),
   articleTeaserView: z.enum(["Kort med ingress", "Teaser"]).optional()
});

export const ZSegmentableEntry = z.union([ZArticleEntry, ZProductCategoryEntry, ZWarningEntry, ZArticleUrlDataEntry]);

export const ZSectionContentEntry = z.union([
   ZSectionEntry,
   ZArticleUrlDataEntry,
   ZRecipeUrlDataEntry,
   ZAdventCalendarSlotUrlDataEntry
]);

export const ArticlesInSection = z.array(ZArticleFundamentalEntry);

export type FullContentEntry = z.infer<typeof ZFullContentEntry>;

export type ImageWithFocusEntry = z.infer<typeof ZImageWithFocusEntry>;

export type ArticleFundamentalEntryType = z.infer<typeof ZArticleFundamentalEntry>;

export type RecipeFundamentalEntryType = z.infer<typeof ZRecipeFundamentalEntry>;

export type ProductCategoryEntryType = z.infer<typeof ZProductCategoryEntry>;

export type SegmentEntryType = z.infer<typeof ZSegmentEntry>;

export type ArticleUrlDataEntryType = z.infer<typeof ZArticleUrlDataEntry>;

export type RecipeUrlDataEntryType = z.infer<typeof ZRecipeUrlDataEntry>;

export type SectionUrlDataEntryType = z.infer<typeof ZSectionUrlDataEntry>;

export type AdventCalendarSlotUrlDataEntryType = z.infer<typeof ZAdventCalendarSlotUrlDataEntry>;

export type ArticleEntryType = z.infer<typeof ZArticleEntry>;

export type SectionEntryType = z.infer<typeof ZSectionEntry>;

export type RecipeEntryType = z.infer<typeof ZRecipeEntry>;

export type FooterEntryType = z.infer<typeof ZFooterEntry>;

export type WarningEntryType = z.infer<typeof ZWarningEntry>;

export type SegmentableEntryType = z.infer<typeof ZSegmentableEntry>;

export type SectionContentEntryType = z.infer<typeof ZSectionContentEntry>;
